import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { catchError, firstValueFrom } from 'rxjs';

import { ValidationService } from '../../src/utils/valibot/validation.service';
import { skipAdminHeaders } from '../authentication/auth-interceptor';
import { DisplayedUserEndpointErrorType, User, UserEndpointError, UserSchema } from './user';

/**
 * The user client is used to fetch user information from the CDF quarkus backend.
 * The access token is automatically injected by the auth-interceptor as header.
 * The backend uses WEN's userinfo endpoint to return relevant information for the frontend.
 */
@Injectable({
  providedIn: 'root',
})
export class UserClient {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);
  private readonly validationService = inject(ValidationService);

  getUserInfo(): Promise<User> {
    const url = 'v1/user';
    this.logger.info(`#CDFTF0024 Fetching user information ${url}`);
    return firstValueFrom(
      this.http.get(url, { context: new HttpContext().set(skipAdminHeaders, true) }).pipe(
        this.validationService.pipeResponse(UserSchema),
        catchError((errorResponse: unknown) => {
          if (errorResponse instanceof HttpErrorResponse) {
            const error = errorResponse.error as unknown;

            if (error instanceof Event) {
              // Cannot handle network errors in a meaningful way, so we just throw the error.
            } else if (error && typeof error === 'object') {
              // If error is an object, the response came from our backend in the expected format.

              const type = 'type' in error && typeof error.type === 'string' ? error.type : undefined;
              const eventId = 'eventId' in error && typeof error.eventId === 'string' ? error.eventId : undefined;

              if (type || eventId) {
                throw new UserEndpointError(type ?? 'UNKNOWN', eventId);
              }
            } else if (errorResponse.status === 403) {
              // A 403 without a body indicates that the request was blocked by the @RolesAllowed annotation.

              throw new UserEndpointError(DisplayedUserEndpointErrorType.MISSING_ROLE);
            }
          }
          this.logger.error('#CDFTF0025 Unable to fetch user information', errorResponse);
          throw errorResponse;
        }),
      ),
    );
  }
}
